import { TranscriptWord } from '../types';
import { SearchNavigationState } from '../hooks/useSearchNavigation';

/**
 * Determines the CSS classes for a word based on its state
 */
const getWordClasses = (
  isCurrentWord: boolean,
  isPlaying: boolean,
  isMatch: boolean,
  isLowConfidence: boolean,
  isInaudible: boolean,
  hasEntities: boolean
): string => {
  const commonClasses = 'p-1 rounded-sm';
  const entitiesClasses = hasEntities
    ? ' border-b-2 border-amber-400 dark:border-amber-500'
    : '';

  // Low confidence styling - different for inaudible vs regular low confidence
  const lowConfidenceClasses = isInaudible
    ? // Inaudible: gray text with strikethrough
      ' opacity-60 line-through text-gray-500 dark:text-gray-400'
    : isLowConfidence
      ? // Low confidence: italic, dashed red underline, 60% opacity
        ' opacity-60 italic border-b border-dashed border-red-400 dark:border-red-500'
      : '';

  // Current word from media player takes precedence
  if (isCurrentWord) {
    if (isPlaying) {
      // Playing - gold highlight
      return `${commonClasses} bg-amber-500 text-black shadow-lg shadow-amber-500/30${lowConfidenceClasses}`;
    } else {
      // Paused - gray highlight to show where playback will resume
      return `${commonClasses} bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300${lowConfidenceClasses}`;
    }
  } else if (isMatch) {
    // Search match highlight (all matches look the same)
    return `${commonClasses} bg-yellow-200 text-black${lowConfidenceClasses}`;
  } else {
    // Default state with hover styles
    return `${commonClasses}${entitiesClasses}${lowConfidenceClasses} cursor-pointer dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-800 hover:text-blue-800 dark:hover:text-blue-400`;
  }
};

/**
 * Renders an array of EditorWords as HTML with each word wrapped in a span
 */
export const renderWordsAsHTML = (
  utteranceId: string,
  words: TranscriptWord[],
  currentWordIndex: number,
  isPlaying: boolean,
  searchState: SearchNavigationState
): string => {
  if (words.length === 0) return '';

  return words
    .map((word, index) => {
      const isCurrentWord = index === currentWordIndex;

      // Check if this word contains search matches
      let isMatch = false;
      if (searchState?.isActive && searchState.query) {
        const wordStartPos = words
          .slice(0, index)
          .reduce((pos, w) => pos + w.text.length + 1, 0);
        const wordEndPos = wordStartPos + word.text.length;

        // Find matches for this utterance and check if any overlap with this word
        const utteranceMatches = searchState.matches.filter(
          (m) => m.utteranceId === utteranceId
        );

        const matchInThisWord = utteranceMatches.find((match) => {
          // Check if the match overlaps with this word's position
          return (
            wordStartPos <= match.matchStartPos &&
            match.matchEndPos <= wordEndPos
          );
        });

        if (matchInThisWord) {
          isMatch = true;
        }
      }

      // Determine word state based on confidence score
      // conf=0: both flags true (inaudible)
      // conf<0.8: isLowConfidence true, isInaudible false
      // conf>=0.8: both flags false (verified)
      const isInaudible = word.confidenceScore === 0;
      const isLowConfidence = word.confidenceScore < 0.8;

      // Get the CSS classes for this word
      const classes = getWordClasses(
        isCurrentWord,
        isPlaying,
        isMatch,
        isLowConfidence,
        isInaudible,
        word.hasEntities
      );

      // Add title attribute for low confidence words to show the actual confidence score
      const titleAttr = isInaudible
        ? ` title="Inaudible"`
        : isLowConfidence
          ? ` title="Low confidence: ${Math.round(word.confidenceScore * 100)}%"`
          : '';

      return `<span data-word-id="${word.id}" class="${classes}"${titleAttr}>${word.text}</span>`;
    })
    .join(' ');
};
